/**
 * The Graph Explorer: what is in the graph, and an open query box.
 *
 * This is the surface for the questions the fixed screens do not cover. Two
 * halves, and the split is deliberate: the left rail answers "what is in here"
 * without anyone typing anything, and clicking any of it writes a runnable
 * query into the editor — which is how someone learns the shape of their own
 * graph without learning Cypher first.
 *
 * The console is read-only and says so before you type, not after you are
 * refused. Both limits that will actually bite — a thousand rows and ten
 * seconds — are stated in the footer where the result count goes, so they read
 * as facts about the tool rather than as errors about your query.
 */

import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { EmptyState, ErrorNotice, Loading, SkeletonRows } from '@/shared/components'
import { Constellation } from '@/shared/components/Constellation'
import { Icon } from '@/shared/components/icons'
import { Dock, DockMain, DockPanel, useDock } from '@/shared/components/dock/Dock'
import { useToast } from '@/shared/components/toast'
import { Chip, SubTabs } from '../components'
import {
  useGraphs,
  useExplorerLimits,
  useExplorerStats,
  useQueryLibrary,
  useRebuildProjection,
  useRunCypher,
  useSaveQuery,
  useDeleteSavedQuery,
} from '../hooks'
import type { CypherEngine, CypherResult } from '../types/brain.types'
import '../styles/brain.css'

type ResultView = 'table' | 'json'
type LibraryTab = 'presets' | 'saved' | 'history'

const STARTER = 'MATCH (r:Record)\nRETURN r.nodeType AS type, count(r) AS records\nORDER BY records DESC'

export function ExplorerPage() {
  // Reachable two ways: from a graph's workspace, where the id is in the path,
  // and from the main nav, where it is not — in which case the first graph is
  // the one to open, exactly as Objects does.
  const { graphId: routeGraphId } = useParams<{ graphId?: string }>()
  const graphs = useGraphs()
  const graphId = routeGraphId ?? graphs.data?.[0]?.graph_id ?? ''
  const toast = useToast()

  const stats = useExplorerStats(graphId)
  const limits = useExplorerLimits(graphId)
  const library = useQueryLibrary(graphId)
  const run = useRunCypher(graphId)
  const save = useSaveQuery(graphId)
  const removeSaved = useDeleteSavedQuery(graphId)
  const rebuild = useRebuildProjection(graphId)

  const [query, setQuery] = useState(STARTER)
  // The counts rail is a dock panel: minimise it and the console takes the width.
  const dock = useDock('brain.explorer', { rail: 'open' })
  const [params, setParams] = useState('{}')
  const [showParams, setShowParams] = useState(false)
  const [view, setView] = useState<ResultView>('table')
  const [tab, setTab] = useState<LibraryTab>('presets')
  const [result, setResult] = useState<CypherResult | null>(null)
  // Which engine answers. `auto` follows what the server has, which is what
  // almost everyone wants; the choice exists because the two engines read
  // different things — the projection can be stale, SQL never is.
  const [engine, setEngine] = useState<CypherEngine>('auto')
  const editorRef = useRef<HTMLTextAreaElement>(null)

  /** Put a query in the editor and focus it, so the next thing is Run. */
  function load(cypher: string) {
    setQuery(cypher)
    editorRef.current?.focus()
  }

  async function execute() {
    let parsed: Record<string, unknown> = {}
    if (showParams && params.trim()) {
      try {
        parsed = JSON.parse(params)
      } catch {
        toast.error(new Error('The parameters are not valid JSON.'), 'Check the parameters')
        return
      }
    }
    const outcome = await run.mutateAsync({ query, parameters: parsed, engine })
    setResult(outcome)
    // A result with nodes in it is worth looking at as a table first; the
    // shape of the columns is what tells you whether the query did what you
    // meant, and JSON is one click away.
    setView('table')
  }

  const rows = result?.rows ?? []
  const fields = result?.fields ?? []

  const headroom = stats.data?.headroom

  /** What `auto` would actually pick, so the header can name it up front. */
  const resolvedEngine: CypherEngine =
    engine !== 'auto' ? engine : (limits.data?.default_engine ?? 'sql')
  const engines = limits.data?.engines ?? []

  const sorted = useMemo(
    () => ({
      types: stats.data?.relationship_types ?? [],
      labels: stats.data?.labels ?? [],
    }),
    [stats.data],
  )

  if (graphs.isLoading) return <Loading label="Loading graphs…" />
  if (!graphId) {
    return (
      <div className="page">
        <div className="page-header hero">
          <Constellation />
          <div className="hero-text">
            <h1>Explorer</h1>
            <p>Ask the graph a question the fixed screens do not answer.</p>
          </div>
        </div>
        <EmptyState
          title="No graph to explore yet"
          icon={<Icon.compass />}
          action={
            <Link className="btn btn-primary" to="/brain">
              Create a knowledge graph
            </Link>
          }
        >
          The Explorer runs read-only queries against one graph. Create one first, then come
          back here.
        </EmptyState>
      </div>
    )
  }

  return (
    <div className="cp-screen">
      <div className="cp-scroll">
        <div className="cp-toolbar">
          <h1>Explorer</h1>
          {/* Which engine will answer, said before anything is run. The old
              "no graph store configured" warning was the whole story when
              Neo4j was the only engine; now it is a detail about one of two. */}
          <Chip tone={resolvedEngine === 'graph' ? 'ok' : 'plain'}>
            {resolvedEngine === 'graph' ? 'Neo4j projection' : 'records · SQL'}
          </Chip>
          {limits.data && <Chip tone="ok">read-only</Chip>}
          <span className="cp-spacer" />
          <button
            className="btn btn-sm"
            onClick={() => stats.refetch()}
            disabled={stats.isFetching}
            title="The counts are cached for a minute; this asks for them now."
          >
            {stats.isFetching ? 'Refreshing…' : 'Refresh counts'}
          </button>
          {/* Only worth offering when there is a projection to rebuild. */}
          {stats.data?.configured && (
          <button
            className="btn btn-sm"
            disabled={rebuild.isPending}
            onClick={async () => {
              try {
                const out = await rebuild.mutateAsync()
                toast.success(
                  'Projection rebuilt',
                  `${out.nodes} records and ${out.edges} relationships written from Postgres.`,
                )
              } catch (error) {
                toast.error(error)
              }
            }}
            title="Rewrite the whole graph projection from the records in Postgres."
          >
            {rebuild.isPending ? 'Rebuilding…' : 'Rebuild projection'}
          </button>
          )}
          <div className="sub2">
            Read-only. Queries are capped at {limits.data?.row_cap ?? 1000} rows and{' '}
            {limits.data?.timeout_s ?? 10} seconds.
          </div>
        </div>

        <Dock className="ex-split" left={dock.state.rail} sizes={{ left: 280 }}>
          {/* ---------------------------------------------------- the rail */}
          <DockPanel
            side="left"
            title="Graph"
            icon={<Icon.graph />}
            state={dock.state.rail}
            onState={(st) => dock.set('rail', st)}
          >
          <aside className="ex-rail">
            {stats.isLoading && <SkeletonRows rows={8} columns={2} label="Counting the graph…" />}
            {stats.error && <ErrorNotice error={stats.error} onRetry={stats.refetch} />}

            {stats.data?.configured === false && (
              <p className="small muted">
                No graph store is configured, so these counts and every query come from the
                records themselves. That is the system of record, so nothing here can be
                stale — the SQL engine runs a subset of Cypher rather than all of it.
              </p>
            )}

            {stats.data && (
              <>
                <div className="ex-sect">
                  <h4>Relationships</h4>
                  {sorted.types.length === 0 && (
                    <p className="small faint">No relationships yet.</p>
                  )}
                  {sorted.types.map((row) => (
                    <button
                      key={row.type}
                      className="ex-count"
                      onClick={() =>
                        load(`MATCH ()-[r:\`${row.type}\`]->()\nRETURN r\nLIMIT 100`)
                      }
                      title={`Start a query on :${row.type}`}
                    >
                      <span className="nm mono truncate">{row.type}</span>
                      <span className="ct mono">{row.count.toLocaleString()}</span>
                    </button>
                  ))}
                </div>

                <div className="ex-sect">
                  <h4>Node labels</h4>
                  {sorted.labels.map((row) => (
                    <button
                      key={row.label}
                      className="ex-count"
                      onClick={() =>
                        load(`MATCH (n:\`${row.label}\`)\nRETURN n\nLIMIT 100`)
                      }
                      title={`Start a query on :${row.label}`}
                    >
                      <span className="nm mono truncate">{row.label}</span>
                      <span className="ct mono">{row.count.toLocaleString()}</span>
                    </button>
                  ))}
                </div>

                {/* The ceiling, shown before it is met. A free Aura instance
                    stops at these numbers, and finding that out during an
                    ingest is the worst time to find it out. */}
                {headroom && stats.data.configured && (
                  <div className="ex-sect">
                    <h4>Free-tier headroom</h4>
                    <Meter
                      label="Records"
                      used={stats.data.node_count}
                      cap={headroom.node_cap}
                      fraction={headroom.nodes_used}
                    />
                    <Meter
                      label="Relationships"
                      used={stats.data.relationship_count}
                      cap={headroom.relationship_cap}
                      fraction={headroom.relationships_used}
                    />
                  </div>
                )}
              </>
            )}
          </aside>
          </DockPanel>

          {/* ------------------------------------------------- the console */}
          <DockMain>
          <section className="ex-console">
            <div className="ex-editorwrap">
              <textarea
                ref={editorRef}
                className="ex-editor mono"
                value={query}
                spellCheck={false}
                aria-label="Cypher query"
                placeholder="MATCH (n:Record) RETURN n LIMIT 25"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault()
                    void execute()
                  }
                }}
              />
              <div className="ex-editorbar">
                <button className="btn btn-sm" onClick={() => setShowParams((v) => !v)}>
                  {showParams ? 'Hide parameters' : 'Parameters'}
                </button>

                {/* Two engines answer the same language against two different
                    things. Which one is a query-by-query choice, not a setting,
                    because "did the projection drift" is a question you answer
                    by running the same query on both. */}
                {engines.length > 0 && (
                  <label className="ex-engine">
                    <span className="small faint">Run against</span>
                    <select
                      className="select"
                      value={engine}
                      aria-label="Which engine answers the query"
                      onChange={(e) => setEngine(e.target.value as CypherEngine)}
                    >
                      <option value="auto">
                        Automatic ({limits.data?.default_engine === 'graph'
                          ? 'Neo4j'
                          : 'records'})
                      </option>
                      {engines.map((option) => (
                        <option
                          key={option.id}
                          value={option.id}
                          disabled={!option.available}
                          title={option.description}
                        >
                          {option.label}
                          {option.available ? '' : ' — not configured'}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <span className="cp-spacer" />
                <span className="small faint">⌘/Ctrl + Enter</span>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={execute}
                  disabled={run.isPending || !query.trim()}
                >
                  {run.isPending ? 'Running…' : 'Run'}
                </button>
              </div>
              {showParams && (
                <textarea
                  className="ex-params mono"
                  value={params}
                  aria-label="Query parameters as JSON"
                  spellCheck={false}
                  onChange={(e) => setParams(e.target.value)}
                />
              )}
            </div>

            {/* --------------------------------------------- the result */}
            {run.isPending && <SkeletonRows rows={6} columns={4} label="Running the query…" />}

            {!run.isPending && result?.engine === 'sql' && result.ok === false && (
              <p className="small faint" style={{ margin: '4px 2px' }}>
                Answered by the SQL engine, which runs a subset of Cypher. A construct it
                does not implement is named rather than ignored — configure a graph store
                for the full language.
              </p>
            )}

            {!run.isPending && result && !result.ok && (
              <div className="ex-error" role="alert">
                <strong>
                  {result.refused_before_running
                    ? 'That query was not run'
                    : result.rate_limited
                      ? 'Too many queries'
                      : result.engine === 'sql'
                        ? 'The query did not run'
                        : 'Neo4j refused the query'}
                </strong>
                {/* Verbatim, including the line and token Neo4j names — that
                    detail is the whole reason to have a console. */}
                <pre className="mono">{result.error}</pre>
              </div>
            )}

            {!run.isPending && result?.ok && (
              <>
                <div className="row-tight" style={{ margin: '10px 0 6px' }}>
                  <SubTabs<ResultView>
                    value={view}
                    onChange={setView}
                    tabs={[
                      { key: 'table', label: 'Table', count: rows.length },
                      { key: 'json', label: 'JSON' },
                    ]}
                  />
                  {/* Which engine answered, on the answer. Two engines reading
                      two different things must never leave the reader guessing
                      which one they are looking at. */}
                  <Chip>
                    {result.engine === 'graph' ? 'Neo4j projection' : 'records · SQL'}
                    {result.duration_ms != null ? ` · ${result.duration_ms}ms` : ''}
                  </Chip>
                  <span className="cp-spacer" />
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => downloadCsv(fields, rows)}
                    disabled={rows.length === 0}
                  >
                    Export CSV
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={async () => {
                      const name = window.prompt('Name this query')
                      if (!name) return
                      try {
                        await save.mutateAsync({ name, query })
                        toast.success('Query saved', name)
                      } catch (error) {
                        toast.error(error)
                      }
                    }}
                  >
                    Save query
                  </button>
                </div>

                {rows.length === 0 ? (
                  <p className="muted small">
                    The query ran and matched nothing. That is an answer — check the labels and
                    relationship types in the rail if you expected rows.
                  </p>
                ) : view === 'table' ? (
                  <div className="table-wrap">
                    <table className="cp-table ex-results">
                      <thead>
                        <tr>
                          {fields.map((field) => (
                            <th key={field}>{field}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr key={index}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex} className="mono small">
                                {render(cell)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <pre className="ex-json mono">{JSON.stringify(rows, null, 2)}</pre>
                )}

                <div className="ex-footer mono">
                  {rows.length} row{rows.length === 1 ? '' : 's'} · {result.duration_ms} ms
                  {result.limit_applied
                    ? ` · no LIMIT was written, so ${result.limit} was applied`
                    : ` · limit ${result.limit}`}
                </div>
              </>
            )}

            {!result && !run.isPending && (
              <p className="muted small" style={{ marginTop: 12 }}>
                Run a query to see results, or pick a relationship type to start.
              </p>
            )}

            {/* ------------------------------------------- the library */}
            <div className="ex-library">
              <SubTabs<LibraryTab>
                value={tab}
                onChange={setTab}
                tabs={[
                  { key: 'presets', label: 'Presets', count: library.data?.presets.length },
                  { key: 'saved', label: 'Saved', count: library.data?.saved.length },
                  { key: 'history', label: 'History', count: library.data?.history.length },
                ]}
              />

              {library.isLoading && <Loading label="Loading the query library…" />}

              {tab === 'presets' &&
                library.data?.presets.map((preset) => (
                  <button
                    key={preset.name}
                    className="ex-preset"
                    onClick={() => load(preset.cypher)}
                  >
                    <span className="nm">{preset.name}</span>
                    <span className="ds">{preset.description}</span>
                  </button>
                ))}

              {tab === 'saved' && (
                <>
                  {library.data?.saved.length === 0 && (
                    <p className="small faint">
                      Nothing saved yet. Run a query worth keeping and choose Save query.
                    </p>
                  )}
                  {library.data?.saved.map((entry) => (
                    <div className="ex-saved" key={entry.query_id}>
                      <button className="ex-preset" onClick={() => load(entry.cypher)}>
                        <span className="nm">{entry.name}</span>
                        <span className="ds">{entry.description ?? entry.cypher}</span>
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() =>
                          removeSaved
                            .mutateAsync(entry.query_id)
                            .catch((error: unknown) => toast.error(error))
                        }
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </>
              )}

              {tab === 'history' && (
                <>
                  {library.data?.history.length === 0 && (
                    <p className="small faint">Nothing run yet in this graph.</p>
                  )}
                  {library.data?.history.map((entry) => (
                    <button
                      key={entry.entry_id}
                      className="ex-preset"
                      onClick={() => load(entry.cypher)}
                    >
                      <span className="nm mono truncate">{entry.cypher.split('\n')[0]}</span>
                      <span className="ds">
                        {entry.ok
                          ? `${entry.row_count} row${entry.row_count === 1 ? '' : 's'} · ${entry.duration_ms} ms`
                          : entry.error}
                      </span>
                    </button>
                  ))}
                </>
              )}
            </div>
          </section>
          </DockMain>
        </Dock>
      </div>
    </div>
  )
}

/** A used-against-cap bar. Turns amber, then red, as the ceiling approaches. */
function Meter({
  label,
  used,
  cap,
  fraction,
}: {
  label: string
  used: number
  cap: number
  fraction: number
}) {
  const tone = fraction > 0.9 ? 'err' : fraction > 0.7 ? 'warn' : 'ok'
  return (
    <div className="ex-meter">
      <div className="ex-meterhead">
        <span>{label}</span>
        <span className="mono">
          {used.toLocaleString()} / {cap.toLocaleString()}
        </span>
      </div>
      <div className="ex-meterbar">
        <span data-tone={tone} style={{ width: `${Math.max(1, fraction * 100)}%` }} />
      </div>
    </div>
  )
}

function render(cell: unknown): string {
  if (cell === null || cell === undefined) return '—'
  if (typeof cell === 'object') return JSON.stringify(cell)
  return String(cell)
}

/** The current result as CSV, quoted the way a spreadsheet expects. */
function downloadCsv(fields: string[], rows: unknown[][]) {
  const escape = (value: unknown) => {
    const text = render(value)
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  const csv = [fields.join(','), ...rows.map((row) => row.map(escape).join(','))].join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = 'graph-query.csv'
  link.click()
  URL.revokeObjectURL(url)
}
